/**
 * Merges report rows from multiple batch responses, deduplicating users.
 * Primary merge key: userId. Fallback: email.
 * Users with neither are treated as unique.
 */
export const mergeReportData = (
  accumulated: IReportRow[],
  newBatch: IReportRow[],
): IReportRow[] => {
  // Build a lookup map from accumulated rows
  const rowMap = new Map<string, IReportRow>();
  const unmatchable: IReportRow[] = [];

  for (const row of accumulated) {
    const key = getRowKey(row);
    if (key) {
      rowMap.set(key, row);
    } else {
      unmatchable.push(row);
    }
  }

  // Merge new batch rows
  for (const row of newBatch) {
    const key = getRowKey(row);

    if (!key) {
      unmatchable.push(row);
      continue;
    }

    const existing = rowMap.get(key);
    if (!existing) {
      rowMap.set(key, row);
      continue;
    }

    // Merge courseProgresses (dedup by _id)
    const existingCourseIds = new Set(existing.courseProgresses?.map((c) => c._id) ?? []);
    const newCourses = (row.courseProgresses ?? []).filter((c) => !existingCourseIds.has(c._id));

    // Merge teams (dedup by _id)
    const existingTeamIds = new Set(existing.teams?.map((t) => t._id) ?? []);
    const newTeams = (row.teams ?? []).filter((t) => !existingTeamIds.has(t._id));

    // Create merged row (avoid mutating input)
    const merged: IReportRow = {
      ...existing,
      courseProgresses: [...(existing.courseProgresses ?? []), ...newCourses],
      teams: [...(existing.teams ?? []), ...newTeams],
    };
    rowMap.set(key, merged);
  }

  return [...rowMap.values(), ...unmatchable];
};

const getRowKey = (row: IReportRow): string | null => {
  if (row.userId) return row.userId;
  if (row.email) return row.email;
  return null;
};
